import { useEffect, useId, useReducer, useRef, useState, type ChangeEvent } from 'react'
import {
  AttachmentUploadError,
  uploadAttachment,
  type AttachmentRow,
  type AttachmentUploadStage,
  type MessagingAttachmentSource,
} from '../../../api/attachments'
import { PexIcon } from '../../../components/icons/PexIcon'
import { useT } from '../../../i18n'
import { messagingI18n } from '../../../i18n/messaging'
import {
  classifyAttachment,
  isMessagingPhotoType,
  MAX_MESSAGE_PHOTOS,
  MESSAGING_PHOTO_TYPES,
} from './messaging-media'

type MessagingPhotoPickerProps = {
  appearance: 'default' | 'messenger'
  source: MessagingAttachmentSource
  selectedRows: AttachmentRow[]
  disabled: boolean
  onUploaded: (row: AttachmentRow) => void
  onRemoveConfirmed: (id: string) => void
  onBusyChange: (busy: boolean) => void
}

type NormalizedUploadError = {
  message: string
  stage?: AttachmentUploadStage
  status?: number
  code?: string
}

type PendingPhoto = {
  id: string
  file: File
  previewUrl: string
  state: 'uploading' | 'failed'
  error?: NormalizedUploadError
}

type PendingAction =
  | { type: 'add'; key: string; items: PendingPhoto[] }
  | { type: 'uploading'; key: string; id: string }
  | { type: 'failed'; key: string; id: string; error: NormalizedUploadError }
  | { type: 'remove'; key: string; id: string }

type PendingState = {
  key: string
  items: PendingPhoto[]
}

function pendingReducer(
  state: PendingState,
  action: PendingAction,
): PendingState {
  switch (action.type) {
    case 'add': {
      const items = state.key === action.key ? state.items : []
      return { key: action.key, items: [...items, ...action.items] }
    }
    case 'uploading':
      return state.key !== action.key
        ? state
        : {
            ...state,
            items: state.items.map((item) =>
              item.id === action.id
                ? { ...item, state: 'uploading', error: undefined }
                : item,
            ),
          }
    case 'failed':
      return state.key !== action.key
        ? state
        : {
            ...state,
            items: state.items.map((item) =>
              item.id === action.id
                ? { ...item, state: 'failed', error: action.error }
                : item,
            ),
          }
    case 'remove':
      return state.key !== action.key
        ? state
        : {
            ...state,
            items: state.items.filter((item) => item.id !== action.id),
          }
  }
}

let nextPendingPhotoId = 0

function pendingPhotoId(): string {
  nextPendingPhotoId += 1
  return `messaging-photo-${nextPendingPhotoId}`
}

export function MessagingPhotoPicker({
  appearance,
  source,
  selectedRows,
  disabled,
  onUploaded,
  onRemoveConfirmed,
  onBusyChange,
}: MessagingPhotoPickerProps) {
  const t = useT(messagingI18n)
  const inputId = useId()
  const [pendingState, dispatch] = useReducer(pendingReducer, {
    key: source.objectId,
    items: [],
  })
  const [selectionError, setSelectionError] = useState<{
    key: string
    message: string | null
  }>({ key: source.objectId, message: null })
  const previewUrls = useRef(new Map<string, string>())
  const cancelledIds = useRef(new Set<string>())
  const sourceKey = useRef(source.objectId)
  const mounted = useRef(true)
  const pendingPhotos =
    pendingState.key === source.objectId ? pendingState.items : []
  const currentSelectionError =
    selectionError.key === source.objectId ? selectionError.message : null
  const isBusy = pendingPhotos.some((item) => item.state === 'uploading')
  const confirmedPhotos = selectedRows.filter(
    (row) => classifyAttachment(row) === 'photo',
  )

  const revokePreview = (id: string) => {
    const previewUrl = previewUrls.current.get(id)
    if (!previewUrl) return
    URL.revokeObjectURL(previewUrl)
    previewUrls.current.delete(id)
  }

  const revokeAllPreviews = () => {
    for (const previewUrl of previewUrls.current.values()) {
      URL.revokeObjectURL(previewUrl)
    }
    previewUrls.current.clear()
  }

  useEffect(() => {
    onBusyChange(isBusy)
  }, [isBusy, onBusyChange])

  useEffect(() => {
    sourceKey.current = source.objectId
    cancelledIds.current.clear()
    revokeAllPreviews()
  }, [source.objectId])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      revokeAllPreviews()
      onBusyChange(false)
    }
  }, [onBusyChange])

  const normalizeError = (error: unknown): NormalizedUploadError => {
    if (error instanceof AttachmentUploadError) {
      return {
        message: error.message,
        stage: error.stage,
        status: error.status,
        code: error.code,
      }
    }
    return { message: t.media.uploadFailed }
  }

  const uploadPhoto = async (
    item: PendingPhoto,
    expectedSourceKey: string,
  ): Promise<boolean> => {
    cancelledIds.current.delete(item.id)
    dispatch({ type: 'uploading', key: expectedSourceKey, id: item.id })
    try {
      const row = await uploadAttachment(
        source.component,
        expectedSourceKey,
        item.file,
        'photo',
      )
      if (
        !mounted.current ||
        sourceKey.current !== expectedSourceKey ||
        cancelledIds.current.has(item.id)
      ) {
        revokePreview(item.id)
        return false
      }
      onUploaded(row)
      revokePreview(item.id)
      dispatch({ type: 'remove', key: expectedSourceKey, id: item.id })
      return true
    } catch (error) {
      if (
        mounted.current &&
        sourceKey.current === expectedSourceKey &&
        !cancelledIds.current.has(item.id)
      ) {
        dispatch({
          type: 'failed',
          key: expectedSourceKey,
          id: item.id,
          error: normalizeError(error),
        })
      }
      return false
    }
  }

  const uploadBatch = async (items: PendingPhoto[]) => {
    const expectedSourceKey = source.objectId
    const results = await Promise.allSettled(
      items.map((item) => uploadPhoto(item, expectedSourceKey)),
    )
    const uploaded = results.some(
      (result) => result.status === 'fulfilled' && result.value,
    )
    if (uploaded && mounted.current && sourceKey.current === expectedSourceKey) {
      await source.refetch().catch(() => undefined)
    }
  }

  const selectPhotos = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (disabled || files.length === 0) return

    setSelectionError({ key: source.objectId, message: null })
    if (files.some((file) => !isMessagingPhotoType(file.type))) {
      setSelectionError({
        key: source.objectId,
        message: t.media.photoTypeError,
      })
      return
    }
    if (
      confirmedPhotos.length + pendingPhotos.length + files.length >
      MAX_MESSAGE_PHOTOS
    ) {
      setSelectionError({
        key: source.objectId,
        message: t.media.photoLimitError,
      })
      return
    }

    const items = files.map((file): PendingPhoto => {
      const id = pendingPhotoId()
      const previewUrl = URL.createObjectURL(file)
      previewUrls.current.set(id, previewUrl)
      return { id, file, previewUrl, state: 'uploading' }
    })
    dispatch({ type: 'add', key: source.objectId, items })
    void uploadBatch(items)
  }

  const removePending = (id: string) => {
    cancelledIds.current.add(id)
    revokePreview(id)
    dispatch({ type: 'remove', key: source.objectId, id })
  }

  const retry = async (item: PendingPhoto) => {
    const expectedSourceKey = source.objectId
    const uploaded = await uploadPhoto(item, expectedSourceKey)
    if (uploaded && mounted.current && sourceKey.current === expectedSourceKey) {
      await source.refetch().catch(() => undefined)
    }
  }

  const panelClass =
    appearance === 'messenger'
      ? 'rounded-xl border border-white/10 bg-white/[0.04] p-2.5 text-slate-100'
      : 'rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-2.5 text-[var(--color-text)]'

  return (
    <section className={panelClass} data-appearance={appearance}>
      <div className="flex items-center justify-between gap-2">
        <label
          htmlFor={inputId}
          aria-disabled={disabled}
          className={`inline-flex min-h-11 items-center gap-2 rounded-lg border px-3 text-xs font-semibold transition focus-within:ring-2 focus-within:ring-[var(--color-focus)] ${
            disabled
              ? 'cursor-not-allowed opacity-40'
              : 'cursor-pointer hover:bg-white/[0.08]'
          } ${
            appearance === 'messenger'
              ? 'border-white/10 bg-white/[0.05]'
              : 'border-[var(--list-control-border)] bg-[var(--list-control-bg)]'
          }`}
        >
          <PexIcon name="image" size="sm" decorative />
          {t.media.addPhotos}
        </label>
        <input
          id={inputId}
          type="file"
          className="sr-only"
          aria-label={t.media.photoInputLabel}
          accept={MESSAGING_PHOTO_TYPES.join(',')}
          multiple
          disabled={disabled}
          onChange={selectPhotos}
        />
        <span className="text-xs text-[var(--color-text-muted)]">
          {confirmedPhotos.length + pendingPhotos.length}/{MAX_MESSAGE_PHOTOS}
        </span>
      </div>

      {currentSelectionError ? (
        <p className="mt-2 text-xs font-medium text-[var(--color-danger)]" role="alert">
          {currentSelectionError}
        </p>
      ) : null}

      {confirmedPhotos.length > 0 || pendingPhotos.length > 0 ? (
        <ul
          className="mt-2 flex max-w-full gap-2 overflow-x-auto pb-1"
          aria-label={t.media.addPhotos}
        >
          {confirmedPhotos.map((row) => (
            <li key={row.id} className="relative h-24 w-24 shrink-0 overflow-hidden rounded-lg border border-white/10 bg-black/20">
              {row.thumbnail_url || row.download_url ? (
                <img
                  src={row.thumbnail_url ?? row.download_url ?? undefined}
                  alt={t.media.photoPreviewLabel(row.original_filename)}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="flex h-full w-full items-center justify-center">
                  <PexIcon name="image" size="lg" decorative />
                </span>
              )}
              <button
                type="button"
                aria-label={t.media.removePhoto(row.original_filename)}
                disabled={disabled}
                onClick={() => onRemoveConfirmed(row.id)}
                className="absolute right-1 top-1 inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/70 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-40"
              >
                <PexIcon name="close" size="xs" decorative className="text-current" />
              </button>
            </li>
          ))}
          {pendingPhotos.map((item) => (
            <li key={item.id} className="relative h-24 w-24 shrink-0 overflow-hidden rounded-lg border border-white/10 bg-black/30">
              <img
                src={item.previewUrl}
                alt={t.media.photoPreviewLabel(item.file.name)}
                className={`h-full w-full object-cover ${item.state === 'uploading' ? 'opacity-55' : 'opacity-75'}`}
              />
              {item.state === 'uploading' ? (
                <span className="absolute inset-x-1 bottom-1 rounded bg-black/75 px-1 py-0.5 text-center text-[10px] text-white" role="status">
                  {t.media.uploading}
                </span>
              ) : (
                <div className="absolute inset-x-1 bottom-1">
                  <button
                    type="button"
                    aria-label={t.media.retryUpload}
                    onClick={() => void retry(item)}
                    className="inline-flex min-h-11 w-full items-center justify-center gap-1 rounded bg-black/80 px-1 text-[10px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  >
                    <PexIcon name="refresh" size="xs" decorative className="text-current" />
                    {t.media.retryUpload}
                  </button>
                  <span className="sr-only" role="alert">
                    {item.error?.message ?? t.media.uploadFailed}
                  </span>
                </div>
              )}
              <button
                type="button"
                aria-label={t.media.removePhoto(item.file.name)}
                onClick={() => removePending(item.id)}
                className="absolute right-1 top-1 inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/70 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <PexIcon name="close" size="xs" decorative className="text-current" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
